import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Plus, Trash2, GripVertical, Save, Menu as MenuIcon, ExternalLink } from 'lucide-react';

function SortableMenuItem({ item, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div className={`flex items-center gap-2 p-3 bg-slate-700 rounded-lg ${isDragging ? 'shadow-lg' : ''}`}>
        <button {...attributes} {...listeners} className="p-1 text-slate-400 hover:text-white cursor-grab">
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={item.label}
          onChange={(e) => onUpdate(item.id, { ...item, label: e.target.value })}
          placeholder="Label"
          className="bg-slate-600 border-slate-500 text-white flex-1"
        />
        <Input
          value={item.url}
          onChange={(e) => onUpdate(item.id, { ...item, url: e.target.value })}
          placeholder="URL (e.g., /about)"
          className="bg-slate-600 border-slate-500 text-white flex-1"
        />
        <Select
          value={item.target || '_self'}
          onValueChange={(val) => onUpdate(item.id, { ...item, target: val })}
        >
          <SelectTrigger className="w-28 bg-slate-600 border-slate-500 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-700 border-slate-600">
            <SelectItem value="_self">Same tab</SelectItem>
            <SelectItem value="_blank">New tab</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => onDelete(item.id)} className="p-2 text-slate-400 hover:text-red-400">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function MenuEditor({ menuName, title }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchMenu();
  }, [menuName]);

  const fetchMenu = async () => {
    try {
      const response = await fetch(`${API_URL}/api/menus/${menuName}`);
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMenu = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/menus/${menuName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        toast.success(`${title} saved!`);
      } else {
        toast.error('Failed to save menu');
      }
    } catch (error) {
      toast.error('Error saving menu');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    setItems([...items, { id: `item_${Date.now()}`, label: '', url: '', target: '_self', children: [] }]);
  };

  const updateItem = (id, updatedItem) => {
    setItems(items.map(item => item.id === id ? updatedItem : item));
  };

  const deleteItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      setItems(arrayMove(items, oldIndex, newIndex));
    }
  };

  if (loading) {
    return <div className="text-center text-slate-400 p-4">Loading...</div>;
  }

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MenuIcon className="w-5 h-5 text-blue-400" />
            <CardTitle className="text-white">{title}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button onClick={addItem} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
            <Button onClick={saveMenu} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {items.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <MenuIcon className="w-8 h-8 mx-auto mb-2" />
            <p>No menu items yet</p>
            <Button onClick={addItem} variant="outline" size="sm" className="mt-2 border-slate-600 text-slate-300">
              <Plus className="w-4 h-4 mr-1" />
              Add First Item
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <SortableMenuItem
                  key={item.id}
                  item={item}
                  onUpdate={updateItem}
                  onDelete={deleteItem}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </CardContent>
    </Card>
  );
}

export default function MenusPage() {
  return (
    <div className="p-6 space-y-6">
      <Toaster position="top-center" theme="dark" />
      
      <div>
        <h1 className="text-2xl font-bold text-white">Menus</h1>
        <p className="text-slate-400">Manage your site navigation menus</p>
      </div>

      <div className="grid gap-6">
        <MenuEditor menuName="header" title="Header Menu" />
        <MenuEditor menuName="footer" title="Footer Menu" />
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Quick Links Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-slate-700 rounded-lg">
              <p className="text-white font-medium">Home</p>
              <p className="text-slate-400">/</p>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg">
              <p className="text-white font-medium">Quick 50KB</p>
              <p className="text-slate-400">/quick-50kb</p>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg">
              <p className="text-white font-medium">CMS Pages</p>
              <p className="text-slate-400">/[slug]</p>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg">
              <p className="text-white font-medium">Blog Posts</p>
              <p className="text-slate-400">/[slug]</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Note: Pages and posts use custom slugs directly. Example: /about-us, /contact, /my-blog-post
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
